// D-2: live status + watcher refresh.
//
// Polls /api/broadcasts and dispatches BROADCAST_LIST_SUCCESS directly —
// never BROADCAST_LIST_REQUEST, which blanks the reducer's list and drops
// the grid to skeletons. Cards are keyed by stream id, so each refresh
// reconciles data in place: no remount, no lost hover/focus.
//
// Polling pauses while the tab is hidden (visibilitychange) and refreshes
// immediately on return, so a long-hidden tab never resumes onto stale
// LIVE cards.
//
// Past-due upcoming streams — the documented D-2 decision: status is
// re-derived from the payload every tick, so a stream the API now reports
// live PROMOTES into the Live section, and one the API stops returning
// (the server excludes it after its 30-minute late window) DROPS. In
// between it stays in "Starting soon" with the countdown badge hidden
// (B-4) — streams routinely start a few minutes late, and hiding a
// stream that is about to go live is worse than a short badge-less wait.
//
// ponytail: a failed background refresh is swallowed — last-good data for
// one more interval beats blanking the grid on a transient blip; the next
// tick retries. Add error surfacing only if refreshes fail persistently.
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { BROADCAST_LIST_SUCCESS } from '../constants/broadcastConstants';

export const REFRESH_INTERVAL_MS = 60000;

export default function useLiveRefresh(intervalMs = REFRESH_INTERVAL_MS) {
  const dispatch = useDispatch();

  useEffect(() => {
    let timer = null;

    const refresh = async () => {
      try {
        const { data } = await axios.get('/api/broadcasts');
        dispatch({ type: BROADCAST_LIST_SUCCESS, payload: data });
      } catch (e) {
        // keep last-good data; retry next tick
      }
    };

    const start = () => {
      if (timer == null) timer = setInterval(refresh, intervalMs);
    };
    const stop = () => {
      clearInterval(timer);
      timer = null;
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        stop();
      } else {
        refresh();
        start();
      }
    };

    if (document.visibilityState !== 'hidden') start();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [dispatch, intervalMs]);
}
